import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { IpCheck, RemoveTestRegion, SmsCheck } from "@/components/admin/ChecklistTools";
import { useAdmin } from "@/admin/AuthContext";

const LINKS = { settings: "/admin/settings", pricing: "/admin/pricing", eligibility: "/admin/eligibility", content: "/admin/content", verification: "/admin/reviews", storage: "/admin/storage" };
const LIGHT = { green: "bg-[var(--cw-success)]", red: "bg-[var(--cw-error)]", unknown: "bg-[var(--cw-border-strong)]" };
const WORD = { green: "Ready", red: "Not ready", unknown: "Unknown" };

function Item({ it, onTick, onReload }) {
  const { admin } = useAdmin();
  return (
    <li className="flex items-start gap-4 py-3" data-testid={`checklist-item-${it.key}`}>
      <span className={`mt-1.5 h-3 w-3 shrink-0 rounded-full ${LIGHT[it.status]}`} role="img" aria-label={WORD[it.status]} data-testid={`checklist-light-${it.key}`} data-status={it.status} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{it.label}</p>
        <p className={`mt-0.5 text-xs leading-5 ${it.key === "message_latency" ? "cw-soft" : "cw-muted"}`} data-testid={`checklist-detail-${it.key}`}>{it.detail}</p>
        {it.manual && (
          <label htmlFor={`ck-${it.key}`} className="mt-2 inline-flex cursor-pointer items-center gap-2 text-xs">
            <Checkbox id={`ck-${it.key}`} checked={it.status === "green"} onCheckedChange={(v) => onTick(it, !!v)} data-testid={`checklist-tick-${it.key}`} />
            <span>I checked this{it.confirmed_by ? ` · ${it.status === "green" ? "ticked" : "unticked"} by ${it.confirmed_by}, ${formatDate(it.confirmed_at)}` : ""}</span>
          </label>
        )}
        {it.key === "test_zip_removed" && <RemoveTestRegion onDone={onReload} />}
        {it.key === "trusted_proxy_hops" && <IpCheck />}
        {it.key === "sms_real" && admin?.role === "owner" && <SmsCheck />}
      </div>
      {LINKS[it.link] && <Link to={LINKS[it.link]} className="cw-brand-deep inline-flex shrink-0 items-center gap-1 rounded text-xs font-medium hover:underline" data-testid={`checklist-link-${it.key}`}>
        {it.link === "verification" ? "Verification" : it.link.charAt(0).toUpperCase() + it.link.slice(1)}<ArrowRight size={12} /></Link>}
    </li>
  );
}

export default function PrelaunchChecklist() {
  const { data, error, loading, reload } = useAdminLoad("/admin/checklist");
  const tick = async (it, confirmed) => {
    try { await api(`/admin/checklist/${it.key}/confirm`, { body: { confirmed } }); toast.success(confirmed ? "Ticked." : "Unticked."); reload(); } catch (e) { toast.error(e.message); }
  };
  const reds = data?.items?.filter((i) => i.status === "red").length || 0;
  return (
    <Panel className="px-6 py-5" data-testid="prelaunch-checklist">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-semibold">Pre-launch checklist</h2><p className="cw-soft mt-1 text-sm">Read from the live server. Only the two manual items can be ticked here.</p></div>
        {data && <Pill tone={data.ready ? "success" : "warning"} data-testid="checklist-ready">{data.ready ? "Ready to launch" : `${reds} not ready`}</Pill>}
      </div>
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <ul className="mt-2 divide-y divide-border">{data?.items?.map((it) => <Item key={it.key} it={it} onTick={tick} onReload={reload} />)}</ul>
      </LoadState>
    </Panel>
  );
}
